import { c as _c } from "react/compiler-runtime";
import { IcoArrowUp16, IcoCloseSmall16, IcoFileText16, IcoFolders16, IcoPaperclip16, IcoSquareFrame16, IcoStop16 } from '@onefootprint/icons';
import type { ResourceContext } from '@onefootprint/request-types/percy';
import { Dropdown, IconButton, LoadingSpinner, Tooltip } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import Placeholder from '@tiptap/extension-placeholder';
import { type Content, type Editor, EditorContent, type JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cx } from 'class-variance-authority';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileViewport from '@/hooks/use-mobile-viewport';
import { filterHiddenPickedFiles, getDroppedFiles, MAX_ATTACHMENT_FILES, readDirectoryFiles } from '@/utils/dropped-files';
import { getFileTypeLabel } from '@/utils/file-type-labels';
import type { SendMessageArgs } from '../../hooks/use-chat-ws';
import { useHorizontalScrollFade } from '../../hooks/use-horizontal-scroll-fade';
import { useResourceContextSelection } from '../../hooks/use-resource-context-selection';
import { createPastedTextFile, isPastedText, shouldAttachPastedText } from '../../utils/pasted-text';
import { presentResource } from '../../utils/resource-presentation';
import { parseSeedText } from '../../utils/seed-text';
import FilePreviewDialog from '../file-preview-dialog';
import { AttachedFileCard, isPreviewableFile } from './attached-file-card';
import AttachmentError from './attachment-error';
import { isMentionPopupOpen, Mention, type MentionAttrs, MentionSuggestion, mentionAttrsToResourceContext } from './mention-extension';
type ChatInputHandle = {
  focus: () => void;
  clear: () => void;
  /** Replace the draft with `text` and send it — for suggestions that ask rather than type. */
  submitText: (text: string) => Promise<void>;
  triggerMention: () => void;
  mentionEntity: (attrs: MentionAttrs) => void;
  // Underlying editor, for programmatic content control (and for driving
  // content changes in jsdom tests, where keystrokes can't reach the editor).
  editor: Editor | null;
};
type ChatInputProps = {
  placeholder?: string;
  disabled?: boolean;
  isThinking?: boolean;
  onSendMessage: (content: string, args: SendMessageArgs) => Promise<void>;
  onInterrupt?: () => void;
  enableAttachments?: boolean;
  // Seed the input with starter text; `seedKey` enables re-seeding with the same text.
  // `{|}` marks the caret position, `\n\n` splits into paragraphs.
  seedText?: string;
  seedKey?: number;
  /** Flashes the border when seeded text lands, pointing at a composer away from what seeded it. */
  flashOnSeed?: boolean;
  initialContent?: Content;
  // Defaults to true. Pass false in disabled skeleton inputs to skip mention-plugin init.
  enableEntityMentions?: boolean;
  // Env the @-mention entity search runs against, sent as a per-request
  // `x-is-live` header. Undefined → the client's global env header.
  isLive?: boolean;
  // `content` is the editor's current doc (preserves @-mentions), so a parent can persist an
  // unsent draft across a remount and re-seed it via `initialContent`.
  onChange?: (params: {
    isEmpty: boolean;
    content: JSONContent;
  }) => void;
  // Replaces the action row with a spinner + label while loading.
  isLoading?: boolean;
  loadingText?: string;
  renderLeadingActions?: (api: {
    disabled?: boolean;
  }) => React.ReactNode;
  renderExtraActions?: (api: {
    triggerMention: () => void;
    disabled?: boolean;
  }) => React.ReactNode;
  // Minimum visible rows before the editor grows.
  rows?: number;
  // Open the attach menu on mount. Snapshot/story escape hatch.
  attachMenuDefaultOpen?: boolean;
};
const ATTACHMENT_ERROR_TIMEOUT_MS = 6000;

// Seed value for `isEmpty` before the editor exists (immediatelyRender: false); the editor
// effect corrects it on mount.
const isContentEmpty = (content: Content | undefined): boolean => typeof content === 'string' ? !content.trim() : !content;
const getUniqueFileName = (fileName: string, existingNames: Set<string>) => {
  if (!existingNames.has(fileName)) return fileName;
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(lastDotIndex) : '';
  let copyIndex = 1;
  let candidate = `${baseName}-${copyIndex}${extension}`;
  while (existingNames.has(candidate)) {
    copyIndex += 1;
    candidate = `${baseName}-${copyIndex}${extension}`;
  }
  return candidate;
};
const dedupeFilesByName = (newFiles: File[], existingFiles: File[]) => {
  const existingNames = new Set(existingFiles.map(file => file.name));
  return newFiles.map(file => {
    const uniqueName = getUniqueFileName(file.name, existingNames);
    existingNames.add(uniqueName);
    if (uniqueName === file.name) return file;
    if (isPastedText(file)) return file.withName(uniqueName);
    return new File([file], uniqueName, {
      type: file.type,
      lastModified: file.lastModified
    });
  });
};
const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({
  placeholder,
  disabled,
  isThinking,
  onSendMessage,
  onInterrupt,
  enableAttachments = true,
  seedText,
  seedKey,
  flashOnSeed = true,
  initialContent,
  enableEntityMentions = true,
  isLive,
  onChange,
  isLoading,
  loadingText,
  renderLeadingActions,
  renderExtraActions,
  rows = 4,
  attachMenuDefaultOpen = false
}, ref) => {
  const {
    t
  } = useTranslation('copilot');
  // The strip is self-contained (see FocusStrip); the editor only needs the effective context
  // for the outgoing message.
  const {
    effectiveContext
  } = useResourceContextSelection();
  const [isEmpty, setIsEmpty] = useState(() => isContentEmpty(initialContent));
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [loadingFileNames, setLoadingFileNames] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const mobileViewport = useMobileViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const {
    ref: attachedFilesRef,
    maskImage: filesMaskImage
  } = useHorizontalScrollFade(attachedFiles.length);
  const [isSeedFlashing, setIsSeedFlashing] = useState(false);

  // The TipTap editor is created once. Its `editorProps.handleKeyDown` and
  // `Placeholder` callbacks capture this render's closure — so anything those
  // handlers read must come through a ref that's updated every render.
  const latestRef = useRef({
    placeholder,
    onChange,
    onSendMessage,
    onInterrupt,
    attachedFiles,
    disabled,
    resourceContext: effectiveContext,
    isLive
  });

  // Disable the composer while a send is in flight — notably the POST /chat_sessions
  // a first message triggers. `mutateAsync` is referentially stable across renders, so
  // `handleSubmit` can list it as a dep without churning its identity, which matters
  // because the editor captures `handleSubmit` once (see `handleKeyDown` below).
  const sendMutation = useMutation({
    mutationFn: ({
      text,
      args
    }: {
      text: string;
      args: SendMessageArgs;
    }) => latestRef.current.onSendMessage(text, args)
  });
  const {
    mutateAsync
  } = sendMutation;
  const isInputDisabled = disabled || sendMutation.isPending;
  latestRef.current = {
    placeholder,
    onChange,
    onSendMessage,
    onInterrupt,
    attachedFiles,
    disabled: isInputDisabled,
    // Only forward the context when the toggle is active, so deactivating the
    // chip stops it being attached to outgoing messages.
    resourceContext: effectiveContext,
    isLive
  };
  const handleFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    if (fileArray.length === 0) return;
    if (fileArray.length > MAX_ATTACHMENT_FILES) {
      setAttachmentError(t($ => $.chat.tooManyFiles, {
        max: MAX_ATTACHMENT_FILES
      }));
      return;
    }
    setAttachedFiles(prev => {
      const filesToAttach = dedupeFilesByName(fileArray, prev);
      // Deduped names are unique across `attachedFiles`, so they're shimmer keys
      // that stay stable when a file is removed mid-shimmer.
      const newNames = new Set(filesToAttach.map(file => file.name));
      setLoadingFileNames(prevNames => new Set([...prevNames, ...newNames]));
      setTimeout(() => {
        setLoadingFileNames(prevNames_0 => {
          const next = new Set(prevNames_0);
          for (const name of newNames) next.delete(name);
          return next;
        });
      }, 600);
      return [...prev, ...filesToAttach];
    });
  };

  // One drop is one gesture: loose files and folder contents attach together,
  // so the per-gesture cap applies to their combined count.
  const attachDroppedFiles = async (looseFiles: File[], entries: FileSystemDirectoryEntry[]) => {
    try {
      const {
        files: folderFiles,
        tooManyFiles
      } = await readDirectoryFiles(entries);
      if (tooManyFiles) {
        setAttachmentError(t($_1 => $_1.chat.tooManyFiles, {
          max: MAX_ATTACHMENT_FILES
        }));
        return;
      }
      const combined = [...looseFiles, ...folderFiles];
      if (combined.length === 0) {
        setAttachmentError(t($_2 => $_2.chat.directoryNoFiles));
        return;
      }
      handleFiles(combined);
    } catch {
      setAttachmentError(t($_0 => $_0.chat.directoryNoFiles));
    }
  };
  const attachPastedFiles = (clipboard: DataTransfer) => {
    const {
      files,
      hasUnreadableDirectory
    } = getDroppedFiles(clipboard);
    if (files.length === 0 && !hasUnreadableDirectory) {
      return false;
    }
    setAttachmentError(hasUnreadableDirectory ? t($_3 => $_3.chat.directoryPasteUnsupported) : null);
    handleFiles(files);
    return true;
  };
  const attachLongPastedText = (clipboard_0: DataTransfer) => {
    const text_0 = clipboard_0.getData('text/plain');
    if (!shouldAttachPastedText(text_0)) {
      return false;
    }
    setAttachmentError(null);
    handleFiles([createPastedTextFile(text_0)]);
    return true;
  };
  const handleSubmit = async () => {
    const {
      disabled: isDisabled,
      resourceContext: context
    } = latestRef.current;
    const text_1 = editorRef.current?.getText({
      blockSeparator: '\n\n'
    }).trim() ?? '';
    const files_0 = latestRef.current.attachedFiles;
    if (!text_1 && !files_0.length || isDisabled) return;
    // Snapshot the friendly name of every @-mention so the ack (and chat history)
    // can render the pill at rest without the FE needing to re-resolve the id.
    const mentions: ResourceContext[] = [];
    editorRef.current?.state.doc.descendants(node => {
      if (node.type.name !== 'mention') return true;
      const ctx = mentionAttrsToResourceContext(node.attrs as MentionAttrs);
      if (ctx) mentions.push(ctx);
      return false;
    });
    await mutateAsync({
      text: text_1,
      args: {
        files: files_0,
        context,
        mentions
      }
    });
    // Only clear the content once the send Promise is resolved and acked
    setAttachedFiles([]);
    setPreviewFile(null);
    setAttachmentError(null);
    editorRef.current?.commands.clearContent();
  };
  const triggerMention = () => {
    editorRef.current?.chain().focus().insertContent('@').run();
  };
  const mentionEntity = (attrs: MentionAttrs) => {
    editorRef.current?.chain().focus('end').insertContent([{
      type: 'mention',
      attrs
    }, {
      type: 'text',
      text: ' '
    }]).run();
  };
  const handleInterrupt = () => {
    latestRef.current.onInterrupt?.();
  };
  const editor = useEditor({
    immediatelyRender: false,
    editable: !isInputDisabled,
    content: initialContent,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onUpdate: ({
      editor: ed
    }) => {
      const nextIsEmpty = ed.getText().trim().length === 0;
      setIsEmpty(nextIsEmpty);
      latestRef.current.onChange?.({
        isEmpty: nextIsEmpty,
        content: ed.getJSON()
      });
    },
    extensions: [StarterKit.configure({
      heading: false,
      codeBlock: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      horizontalRule: false,
      code: false,
      dropcursor: false,
      bold: false,
      italic: false,
      strike: false,
      link: false
    }), Placeholder.configure({
      placeholder: () => latestRef.current.placeholder ?? '',
      // Show the placeholder even when the editor is disabled (e.g. loading
      // skeleton): TipTap defaults this to true, which hides the placeholder
      // entirely when `editable=false`.
      showOnlyWhenEditable: false,
      showOnlyCurrent: false
    }), ...(enableEntityMentions ? [Mention, MentionSuggestion.configure({
      getIsLive: () => latestRef.current.isLive
    })] : [])],
    editorProps: {
      attributes: {
        class: 'w-full bg-transparent text-body-2 text-primary focus:outline-none'
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          // While the @mention popup is open, let the suggestion plugin handle Enter
          // (selects the highlighted item). Otherwise, submit.
          if (isMentionPopupOpen(editorRef.current)) return false;
          event.preventDefault();
          handleSubmit();
          return true;
        }
        return false;
      },
      handlePaste: (_view_0, event_0) => {
        const clipboard_1 = event_0.clipboardData;
        if (!enableAttachments || !clipboard_1) {
          return false;
        }
        // Anything not handled here, like short text, falls through to the editor.
        const handled = attachPastedFiles(clipboard_1) || attachLongPastedText(clipboard_1);
        if (handled) event_0.preventDefault();
        return handled;
      }
    }
  });

  // Stable ref to the latest editor for stable callbacks above. Also syncs `isEmpty`
  // from the editor on (re)mount: when `initialContent` re-seeds a persisted draft,
  // `onUpdate` doesn't fire, so the send-button state must be read from the editor
  // itself (`isEmpty` handles rich content and @-mentions).
  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
    if (editor) setIsEmpty(editor.isEmpty);
  }, [editor]);

  // iOS scrolls only far enough to show the caret, which can leave the rest of the composer,
  // send button included, under the keyboard.
  useEffect(() => {
    if (isFocused && mobileViewport?.keyboardOpen) rootRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [isFocused, mobileViewport]);
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !isInputDisabled) editor.setEditable(!isInputDisabled);
  }, [editor, isInputDisabled]);
  useEffect(() => {
    if (!attachmentError) return;
    const timer = setTimeout(() => setAttachmentError(null), ATTACHMENT_ERROR_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [attachmentError]);

  // Placeholder caches decorations until a document or selection transaction.
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr.setSelection(editor.state.selection));
  }, [editor, placeholder]);
  useEffect(() => {
    if (seedText === undefined || !editor) return;
    const {
      html,
      cursorPos
    } = parseSeedText(seedText);
    // Preserve trailing whitespace so a caret marker at paragraph end doesn't snap to the next paragraph.
    editor.commands.setContent(html, {
      parseOptions: {
        preserveWhitespace: 'full'
      }
    });
    setIsSeedFlashing(true);
    const focusTimer = setTimeout(() => editor.commands.focus(cursorPos ?? 'end'), 100);
    const flashTimer = setTimeout(() => setIsSeedFlashing(false), 2500);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(flashTimer);
    };
  }, [seedKey, editor]);
  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.commands.focus('end'),
    clear: () => editorRef.current?.commands.clearContent(),
    submitText: async (text_2: string) => {
      editorRef.current?.commands.setContent(text_2);
      await handleSubmit();
    },
    triggerMention,
    mentionEntity,
    editor
  }), [triggerMention, mentionEntity, editor]);
  const canSend = !isInputDisabled && (!isEmpty || attachedFiles.length > 0);
  // Stop (interrupt) takes over the send slot only when the agent is busy and there's
  // nothing staged to send — and never as a dead button without an interrupt handler.
  // With text staged, the button stays "send" so a follow-up can be queued mid-turn.
  const showStopButton = !!isThinking && isEmpty && !!onInterrupt;
  const removeFile = (index: number) => {
    setAttachedFiles(prev_0 => {
      const removed = prev_0[index];
      if (removed) setPreviewFile(current => current === removed ? null : current);
      return prev_0.filter((_, i) => i !== index);
    });
  };
  const pasteInline = (content: string, index_0: number) => {
    removeFile(index_0);
    // `pasteText` builds a clipboard-less event, so `handlePaste` leaves it to the editor.
    editorRef.current?.commands.focus();
    editorRef.current?.view.pasteText(content);
  };
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop container
    <div ref={rootRef} className={cx('relative w-full scroll-mb-3', isInputDisabled && 'opacity-50')} onDragOver={e => {
      if (!enableAttachments) return;
      e.preventDefault();
      setIsDragOver(true);
    }} onDragLeave={e_0 => {
      if (!enableAttachments) return;
      if (e_0.currentTarget.contains(e_0.relatedTarget as Node)) return;
      setIsDragOver(false);
    }} onDrop={e_1 => {
      if (!enableAttachments) return;
      e_1.preventDefault();
      setIsDragOver(false);
      const {
        files: files_1,
        directoryEntries,
        hasUnreadableDirectory: hasUnreadableDirectory_0
      } = getDroppedFiles(e_1.dataTransfer);
      setAttachmentError(hasUnreadableDirectory_0 ? t($_4 => $_4.chat.directoryPasteUnsupported) : null);
      if (directoryEntries.length > 0) {
        void attachDroppedFiles(files_1, directoryEntries);
      } else {
        handleFiles(files_1);
      }
    }}>
        <FocusStrip />
        {/* The border is on this inner element so the strip above can sit outside it. */}
        <div className={cx('relative flex max-h-[min(40dvh,20rem)] w-full flex-col border rounded-2xl bg-elevated shadow-[0_1px_3px_var(--overlay-darken-2)] transition-colors duration-700', isSeedFlashing && flashOnSeed ? 'border-secondary' : 'border-tertiary')}>
          {isDragOver && <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-primary/90 pointer-events-none">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-inverted shadow-[0_0_0_3px_var(--background-color-senary)]">
                  <IcoFileText16 color="quinary" />
                </div>
                <span className="text-heading-4 text-primary">{t($_5 => $_5.chat.dropOverlay)}</span>
              </div>
            </div>}
          <div className="min-h-0 overflow-y-auto overscroll-contain rounded-t-2xl mb-3">
            {attachedFiles.length > 0 && <div ref={attachedFilesRef} className="flex gap-2 overflow-x-auto pt-3 pb-2 [&::-webkit-scrollbar]:hidden" style={{
            scrollbarWidth: 'none',
            maskImage: filesMaskImage,
            WebkitMaskImage: filesMaskImage
          }}>
                {attachedFiles.map((file_0, index_1) => {
              const typeLabel = getFileTypeLabel(file_0.name);
              return <AttachedFileCard key={`${file_0.name}-${file_0.size}-${file_0.lastModified}`} file={file_0} isLoading={loadingFileNames.has(file_0.name)} typeLabel={typeLabel} className={cx(index_1 === 0 && 'ml-4', index_1 === attachedFiles.length - 1 && 'mr-4')} previewable={isPreviewableFile(file_0)} onPreview={() => setPreviewFile(file_0)} onRemove={() => removeFile(index_1)} onPasteInline={isPastedText(file_0) ? () => pasteInline(file_0.content, index_1) : undefined} />;
            })}
              </div>}
            {previewFile && <FilePreviewDialog open onClose={() => setPreviewFile(null)} fileName={previewFile.name} getFileContent={async () => ({
            blob: previewFile,
            mimeType: previewFile.type || 'application/octet-stream'
          })} />}
            {attachmentError && <AttachmentError message={attachmentError} dismissLabel={t($_6 => $_6.chat.dismissWarning)} durationMs={ATTACHMENT_ERROR_TIMEOUT_MS} onDismiss={() => setAttachmentError(null)} />}
            <div className="relative px-4 pt-3">
              <EditorContent editor={editor} style={{
              ['--chat-input-rows' as string]: rows
            }} className="chat-input-editor w-full" />
            </div>
          </div>
          <div className="relative shrink-0">
            {isLoading && <div className="absolute inset-0 flex items-center justify-between gap-2 px-4 pb-2">
                {loadingText && <p className="min-w-0 truncate text-body-2 text-shimmer">{loadingText}</p>}
                <div className="ml-auto flex size-8 shrink-0 items-center justify-center md:size-7">
                  <LoadingSpinner size="large" color="primary" />
                </div>
              </div>}
            {/* Keep controls in flow so loading preserves the height of wrapped actions. */}
            <div aria-hidden={isLoading} inert={isLoading} className={cx('flex flex-wrap items-center gap-1 px-3 pb-2', {
            invisible: isLoading
          })}>
              {renderLeadingActions?.({
              disabled: isInputDisabled
            })}
              <div className="ml-auto flex items-center gap-2">
                {enableAttachments && <>
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e_2 => {
                  if (e_2.target.files?.length) {
                    setAttachmentError(null);
                    handleFiles(e_2.target.files);
                  }
                  e_2.target.value = '';
                }} />
                    <input ref={folderInputRef} type="file" webkitdirectory="" className="hidden" onChange={e_3 => {
                  if (e_3.target.files?.length) {
                    const visible = filterHiddenPickedFiles(e_3.target.files);
                    if (visible.length === 0) {
                      setAttachmentError(t($_7 => $_7.chat.directoryNoFiles));
                    } else {
                      setAttachmentError(null);
                      handleFiles(visible);
                    }
                  }
                  e_3.target.value = '';
                }} />
                    {/* modal would scroll-lock and collapse the docked chat panel (see chat-widget's menu). */}
                    <Dropdown.Root modal={false} defaultOpen={attachMenuDefaultOpen}>
                      <Tooltip content={t($_8 => $_8.chat.attach)}>
                        <Dropdown.Trigger asChild>
                          <IconButton aria-label={t($_9 => $_9.chat.attach)} className="rounded-full! max-md:size-8" icon={IcoPaperclip16} variant="ghost" size="compact" disabled={isInputDisabled} />
                        </Dropdown.Trigger>
                      </Tooltip>
                      <Dropdown.Portal>
                        {/* side="top": the composer sits at the bottom of the viewport. */}
                        <Dropdown.Content side="top" align="start" sideOffset={4} className="min-w-[180px]">
                          {/* Native pickers need the click inside the user-activation stack —
                           synchronous in onSelect, no preventDefault/setTimeout. */}
                          <Dropdown.Item size="compact" iconLeft={IcoFileText16} onSelect={() => fileInputRef.current?.click()}>
                            {t($_10 => $_10.chat.attachFiles)}
                          </Dropdown.Item>
                          <Dropdown.Item size="compact" iconLeft={IcoFolders16} onSelect={() => folderInputRef.current?.click()}>
                            {t($_11 => $_11.chat.attachFolder)}
                          </Dropdown.Item>
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  </>}
                <FocusToggle disabled={isInputDisabled} />
                {renderExtraActions?.({
                triggerMention,
                disabled: isInputDisabled
              })}
                <div className="shrink-0">
                  {showStopButton ? <Tooltip content={t($_12 => $_12.chat.interrupt)}>
                      <button type="button" aria-label={t($_13 => $_13.chat.interrupt)} className="flex items-center justify-center rounded-full size-8 md:size-7 transition-colors cursor-pointer disabled:cursor-default bg-neutral-inverted" onClick={handleInterrupt} disabled={isInputDisabled}>
                        <IcoStop16 color="quinary" className="scale-[0.8]" />
                      </button>
                    </Tooltip> : <Tooltip content={t($_14 => $_14.chat.send)}>
                      <button type="button" aria-label={t($_15 => $_15.chat.send)} className={cx('flex items-center justify-center rounded-full size-8 md:size-7 transition-colors cursor-pointer disabled:cursor-default', canSend ? 'bg-neutral-inverted' : 'bg-senary opacity-50')} onClick={handleSubmit} disabled={!canSend}>
                        <IcoArrowUp16 color={canSend ? 'quinary' : undefined} />
                      </button>
                    </Tooltip>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
});

/** Shared by the strip and the toggle so both agree on whether there is a page to focus. */
const useFocusedPage = () => {
  const $ = _c(3);
  const {
    t
  } = useTranslation("copilot");
  const {
    context
  } = useResourceContextSelection();
  let t0;
  if ($[0] !== context || $[1] !== t) {
    t0 = context ? presentResource(context, t) : null;
    $[0] = context;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

/** Attaches the page you're on to what you send. Absent off a resource route, where there is none. */
const FocusToggle = t0 => {
  const $0 = _c(11);
  const {
    disabled
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const {
    isActive,
    setActive
  } = useResourceContextSelection();
  const presentation = useFocusedPage();
  let t1;
  if ($0[0] !== isActive) {
    t1 = $ => isActive ? $.chat.focus.on : $.chat.focus.off;
    $0[0] = isActive;
    $0[1] = t1;
  } else {
    t1 = $0[1];
  }
  let t2;
  if ($0[2] !== t || $0[3] !== t1) {
    t2 = t(t1);
    $0[2] = t;
    $0[3] = t1;
    $0[4] = t2;
  } else {
    t2 = $0[4];
  }
  const label = t2;
  let t3;
  if ($0[5] !== disabled || $0[6] !== isActive || $0[7] !== label || $0[8] !== presentation || $0[9] !== setActive) {
    t3 = presentation ? <Tooltip content={label}><button type="button" aria-label={label} aria-pressed={isActive} disabled={disabled} onClick={() => setActive(!isActive)} className={cx("flex size-8 md:size-7 shrink-0 items-center justify-center rounded-full text-primary transition-colors cursor-pointer disabled:cursor-default", isActive ? "bg-senary/70" : "hover:bg-hover")}><IcoSquareFrame16 /></button></Tooltip> : null;
    $0[5] = disabled;
    $0[6] = isActive;
    $0[7] = label;
    $0[8] = presentation;
    $0[9] = setActive;
    $0[10] = t3;
  } else {
    t3 = $0[10];
  }
  return t3;
};

/**
 * Names the focused page above the composer. Collapses its height rather than unmounting, so
 * whatever sits above it slides instead of jumping.
 */
const FocusStrip = () => {
  const $ = _c(19);
  const {
    t
  } = useTranslation("copilot");
  const {
    isActive,
    clearSelection
  } = useResourceContextSelection();
  const presentation = useFocusedPage();
  if (!presentation) {
    return null;
  }
  const resourceLabel = presentation.name ?? presentation.label;
  const selected = presentation.subtitle ?? null;
  const t0 = !isActive;
  const t1 = !isActive;
  const t2 = isActive ? "h-[47px] -mb-4 pt-1.5 opacity-100" : "h-0 pt-0 opacity-0";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("group/strip overflow-hidden rounded-t-2xl bg-neutral pl-3.5 pr-4 transition-[height,margin,padding,opacity] duration-200 ease-out motion-reduce:transition-none", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== presentation.icon) {
    t4 = <span className="flex size-4 shrink-0 items-center justify-center text-primary">{presentation.icon}</span>;
    $[2] = presentation.icon;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== resourceLabel) {
    t5 = <span className="min-w-0 truncate text-caption-3 font-medium text-primary">{resourceLabel}</span>;
    $[4] = resourceLabel;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== clearSelection || $[7] !== selected || $[8] !== t) {
    t6 = selected && <><span aria-hidden={true} className="shrink-0 text-caption-3 text-quaternary">/</span><span className="min-w-0 truncate text-caption-3 font-medium text-primary">{selected}</span><button type="button" title={t(_temp, {
        node: selected
      })} aria-label={t(_temp2, {
        node: selected
      })} onClick={clearSelection} className="ml-auto flex shrink-0 cursor-pointer items-center text-tertiary opacity-0 max-md:opacity-60 transition-opacity group-hover/strip:opacity-60 group-hover/strip:hover:opacity-100 focus-visible:opacity-100"><IcoCloseSmall16 /></button></>;
    $[6] = clearSelection;
    $[7] = selected;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex h-5 items-center gap-2">{t4}{t5}{t6}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t0 || $[15] !== t1 || $[16] !== t3 || $[17] !== t7) {
    t8 = <div aria-hidden={t0} inert={t1} className={t3}>{t7}</div>;
    $[14] = t0;
    $[15] = t1;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
export type { ChatInputHandle, ChatInputProps };
export default ChatInput;
function _temp($) {
  return $.chat.focus.removeSelection;
}
function _temp2($_0) {
  return $_0.chat.focus.removeSelection;
}